/**
 * PlanAndBillingBlock — Profile card. Info layer only.
 *
 * Layout (founder-specced 3-region horizontal):
 *
 *   ┌─────────────────────────────────────────────────────────────────┐
 *   │ YOUR PLAN                                                        │
 *   │ ┌───────────┐ ┌────────────────────────────────┐ ┌────────────┐ │
 *   │ │ Tier      │ │  Current promos                │ │  Manage    │ │
 *   │ │ Status    │ │  • Founder Lifetime - $249...  │ │  My Sub →  │ │
 *   │ │           │ │  • Hero Lifetime - $999...     │ │            │ │
 *   │ │           │ │  For more details, tap Manage  │ │            │ │
 *   │ │           │ │  My Subscription.              │ │            │ │
 *   │ └───────────┘ └────────────────────────────────┘ └────────────┘ │
 *   └─────────────────────────────────────────────────────────────────┘
 *
 * Architecture:
 *   - Profile = info layer ("what's true about me right now")
 *   - /pricing = action layer ("what I can change")
 *
 * The "My Subscription" pill is the ONLY active link on this card. The
 * promo block is contextual info — users learn promos exist + are
 * pointed to /pricing for detail/action. Pill is right-anchored
 * matching the app's established inline-right pill pattern (Coaches +,
 * dialog actions, etc.). Mobile (<md) stacks regions vertically.
 *
 * Filename stays `SubscriptionBlock.jsx` for import compatibility.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CreditCard, Crown, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!isFinite(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86_400_000));
};

const PlanAndBillingBlock = () => {
  const [sub, setSub] = useState(null);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/payments/me`)
      .then((r) => { if (alive) setSub(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    axios.get(`${API}/payments/founder/seats`)
      .then((r) => { if (alive) setSeats(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!sub) return null;

  // Normalize the tier reading. Lifetime trumps stripe status.
  const tier = sub.subscription_tier || 'egnite';
  const status = sub.subscription_status;
  const lifetime = sub.lifetime === true || ['founder', 'hero', 'comp'].includes(tier);
  const isFounder = tier === 'founder';
  const isHero = tier === 'hero';
  const seatNum = sub.founder_seat_number;
  const periodEnd = fmtDate(sub.current_period_end || sub.paid_until);
  const trialEnd = fmtDate(sub.trial_end);
  const trialDays = daysUntil(sub.trial_end);
  const cancelAtPeriodEnd = !!sub.cancel_at_period_end;

  // Tier label — left column heading.
  const tierLabel = (() => {
    if (isFounder) return seatNum ? `Founder · Seat #${seatNum}` : 'Founder';
    if (isHero) return 'Hero · Lifetime';
    if (tier === 'comp') return 'Comp · Lifetime';
    if (tier === 'enferno') {
      const cadence = sub.plan_id === 'enferno-annual' ? 'Annual' : sub.plan_id === 'enferno-monthly' ? 'Monthly' : '';
      return cadence ? `Enferno (${cadence})` : 'Enferno';
    }
    if (status === 'trialing') {
      return `Trial — ${trialDays != null ? `${trialDays} day${trialDays === 1 ? '' : 's'} left` : 'in progress'}`;
    }
    return 'Egnite (Free)';
  })();

  // Status sub-line under the tier label.
  const statusLine = (() => {
    if (isFounder) return "Lifetime Enferno access. You're one of the people who made this real.";
    if (isHero || tier === 'comp') return 'Lifetime Enferno access. Thanks for being here.';
    if (tier === 'enferno') {
      if (status === 'trialing') return trialEnd ? `On 14-day trial · ends ${trialEnd}` : 'On 14-day trial';
      if (status === 'active') {
        if (cancelAtPeriodEnd && periodEnd) return `Cancels ${periodEnd}`;
        return periodEnd ? `Renews ${periodEnd}` : 'Active';
      }
      if (status === 'past_due') return 'Past due — see /pricing to update billing';
      if (status === 'canceled') return 'Canceled';
      return status || 'Active';
    }
    if (status === 'trialing') return trialEnd ? `Trial ends ${trialEnd}` : null;
    return 'Free forever.';
  })();

  // Promo bullet list — short contextual lines. Each promo hides for
  // users who can't act on it (e.g., Founders don't see Founder bullet,
  // Heroes don't see Hero bullet). Future promos plug in here.
  const promoBullets = [];
  if (seats?.available && !isFounder && !lifetime) {
    // Egnite / Trial / Enferno paid users see the Founder offer.
    promoBullets.push(`Founder Lifetime — $249 once — ${seats.remaining} of ${seats.total} left`);
  }
  // Hero promo is gated behind future feature flag; left as a placeholder
  // pattern for when it launches:
  //   if (heroSeats?.available && !isHero && !lifetime) {
  //     promoBullets.push(`Hero Lifetime — $999 once — ${heroSeats.remaining} of ${heroSeats.total} left`);
  //   }

  // VIP support email surfaces for paid tiers only (any active sub or
  // lifetime grant). Free Egnite users don't see it.
  const showVipSupport = lifetime || tier === 'enferno' || status === 'trialing';

  // Visual treatment per tier state. Lifetime users get amber; everyone
  // else gets neutral surface.
  const cardOuter = lifetime
    ? 'mt-6 bg-gradient-to-br from-amber-900/15 to-enf-surface-deep border border-amber-700/40 rounded-xl p-5 sm:p-6'
    : 'mt-6 bg-enf-surface-deep border border-enf-surface-border rounded-xl p-5 sm:p-6';
  const headingIcon = lifetime
    ? <Crown className="w-4 h-4 text-amber-400" />
    : <CreditCard className="w-4 h-4 text-enf-text-secondary" />;
  const headingClass = lifetime
    ? 'text-sm font-semibold uppercase tracking-widest text-amber-400'
    : 'text-sm font-semibold uppercase tracking-widest text-enf-text-secondary';

  return (
    <div className={cardOuter} data-testid="plan-block">
      <div className="flex items-center gap-2 mb-4">
        {headingIcon}
        <h3 className={headingClass}>Your plan</h3>
      </div>

      {/* 3-region horizontal layout. Stacks vertically below md. */}
      <div className="flex flex-col md:flex-row md:items-center md:gap-6 gap-4">
        {/* LEFT — tier label + status sub-line */}
        <div className="md:w-56 md:flex-shrink-0">
          <div
            className="font-display text-2xl tracking-wide text-enf-text-primary mb-1"
            data-testid="plan-block-tier-label"
          >
            {tierLabel}
          </div>
          {statusLine && (
            <p className="text-sm text-enf-text-secondary" data-testid="plan-block-status-line">
              {statusLine}
            </p>
          )}
          {showVipSupport && (
            <p className="text-xs text-enf-text-muted mt-2" data-testid="plan-block-vip-support">
              VIP support: <a href="mailto:support.vip@enferno.app" className="text-enf-accent-fg hover:text-enf-text-primary transition-colors">support.vip@enferno.app</a>
            </p>
          )}
        </div>

        {/* CENTER — embedded promos block. Hidden when no promos apply
            to the current user (e.g., Founders see no promos). */}
        {promoBullets.length > 0 && (
          <div
            className="md:flex-1 bg-enf-surface-mid/40 border border-enf-surface-border rounded-lg p-4"
            data-testid="plan-block-promos"
          >
            <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">
              Current promos
            </div>
            <ul className="space-y-1.5 mb-3" data-testid="plan-block-promo-list">
              {promoBullets.map((b, i) => (
                <li
                  key={i}
                  className="text-sm text-enf-text-secondary flex items-start gap-2"
                  data-testid={`plan-block-promo-item-${i}`}
                >
                  <span className="text-amber-400 mt-1 flex-shrink-0">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-enf-text-muted italic">
              For more details, tap Manage My Subscription.
            </p>
          </div>
        )}

        {/* RIGHT — the one active link. Anchored right on md+,
            full-width on mobile. */}
        <div className="md:flex-shrink-0">
          <Link
            to="/pricing"
            data-testid="plan-block-my-subscription"
            className="inline-flex items-center justify-center gap-2 w-full md:w-auto px-5 py-2.5 rounded-md text-sm font-semibold bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border text-enf-text-primary transition-colors whitespace-nowrap"
          >
            Manage My Subscription <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PlanAndBillingBlock;
